/**
 * First-login MFA setup wizard.
 *
 * Shown on the login page when /auth/login returns requires_mfa_setup=true
 * (a user — e.g. freshly invited — who has set a password but has no MFA yet).
 *
 * Step 0 (choose): pick a passkey (recommended) or an authenticator app. The
 *   passkey option only appears when the browser supports WebAuthn; otherwise the
 *   wizard opens straight on the authenticator (TOTP) step.
 * Passkey branch: run the WebAuthn ceremony, then land on the backup-codes screen.
 * TOTP branch (scan): scan the QR / enter the secret, confirm with a 6-digit code.
 * Final (backup): save the one-time backup codes, then finish (now authenticated).
 *
 * Both factors satisfy the MFA requirement and both converge on the same
 * backup-codes screen.
 */
import { useEffect, useState, FormEvent } from 'react'
import api, { getErrorMessage } from '../services/api'
import { mfaSetupBegin, mfaSetupVerify, mfaSetupPasskey } from '../services/mfaSetup'
import { isPasskeySupported } from '../services/webauthn'
import { qrCodeSvgDataUrl } from '../utils/qrCode'

interface Props {
  sessionId: string
  /** Called once setup completes and the session is authenticated. */
  onComplete: () => void
  /** Called to abandon setup and return to the login screen. */
  onCancel: () => void
}

type Step = 'choose' | 'scan' | 'backup'

export default function MfaSetupWizard({ sessionId, onComplete, onCancel }: Props) {
  const passkeyOk = isPasskeySupported()
  // With a passkey-capable browser, open on the method chooser; otherwise the
  // authenticator-app path is the only option, so go straight to it.
  const [step, setStep] = useState<Step>(passkeyOk ? 'choose' : 'scan')
  const [secret, setSecret] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [totpBegun, setTotpBegun] = useState(false)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [savedConfirmed, setSavedConfirmed] = useState(false)
  const [beginLoading, setBeginLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [error, setError] = useState('')

  // Lazily mint the TOTP secret only when the user actually enters the scan step,
  // so choosing the passkey path never provisions an unused authenticator device.
  useEffect(() => {
    if (step !== 'scan' || totpBegun) return
    let active = true
    setTotpBegun(true)
    setBeginLoading(true)
    mfaSetupBegin(sessionId)
      .then(async (r) => {
        if (!active) return
        setSecret(r.secret)
        try {
          const url = await qrCodeSvgDataUrl(r.qr_uri)
          if (active) setQrDataUrl(url)
        } catch {
          /* QR render failed — the manual secret below still works */
        }
      })
      .catch((e) => {
        if (active) setError(getErrorMessage(e))
      })
      .finally(() => {
        if (active) setBeginLoading(false)
      })
    return () => {
      active = false
    }
  }, [step, totpBegun, sessionId])

  async function handlePasskeySetup() {
    setError('')
    setPasskeyLoading(true)
    try {
      const result = await mfaSetupPasskey(sessionId)
      if (result.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${result.access_token}`
      }
      setBackupCodes(result.backup_codes)
      setStep('backup')
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setPasskeyLoading(false)
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await mfaSetupVerify({ session_id: sessionId, code })
      if (result.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${result.access_token}`
      }
      setBackupCodes(result.backup_codes)
      setStep('backup')
    } catch (err) {
      setError(getErrorMessage(err))
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  function copyCodes() {
    navigator.clipboard?.writeText(backupCodes.join('\n')).catch(() => {})
  }

  // ===== Backup codes screen (shared by both branches) =====
  if (step === 'backup') {
    return (
      <div className="mt-8 space-y-6">
        <div className="alert alert-success">
          Multi-factor authentication is set up. Save these one-time backup codes now.
        </div>
        <p className="text-sm text-gray-600">
          Each code works once if you lose access to your authenticator or passkey. They won&apos;t
          be shown again.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-md bg-gray-50 border border-gray-200 p-4 font-mono text-sm">
          {backupCodes.map((c) => (
            <div key={c} className="text-center tracking-widest text-gray-800">{c}</div>
          ))}
        </div>
        <button type="button" onClick={copyCodes} className="text-sm text-primary-600 hover:text-primary-500 font-medium">
          Copy all codes
        </button>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={savedConfirmed}
            onChange={(e) => setSavedConfirmed(e.target.checked)}
          />
          <span>I&apos;ve saved these backup codes somewhere safe.</span>
        </label>
        <button
          type="button"
          disabled={!savedConfirmed}
          onClick={onComplete}
          className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue to the app
        </button>
      </div>
    )
  }

  // ===== Method chooser (step 0) =====
  if (step === 'choose') {
    return (
      <div className="mt-8 space-y-6">
        <div className="alert alert-warning text-sm">
          Set up multi-factor authentication to finish signing in. Choose how you&apos;d like to
          protect your account.
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <button
          type="button"
          disabled={passkeyLoading}
          onClick={handlePasskeySetup}
          className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {passkeyLoading ? 'Waiting for passkey…' : 'Set up a passkey (recommended)'}
        </button>
        <p className="-mt-3 text-xs text-gray-500">
          Use your device&apos;s fingerprint, face, screen lock, or a security key. Nothing to type,
          and nothing to lose.
        </p>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gray-50 text-gray-500">or</span>
          </div>
        </div>

        <button
          type="button"
          disabled={passkeyLoading}
          onClick={() => {
            setError('')
            setStep('scan')
          }}
          className="w-full btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Use an authenticator app
        </button>

        <div className="text-center">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
          >
            Back to sign in
          </button>
        </div>
      </div>
    )
  }

  // ===== Authenticator-app (TOTP) step =====
  return (
    <div className="mt-8 space-y-6">
      <div className="alert alert-warning text-sm">
        Scan the code below with an authenticator app (Google Authenticator, Authy, 1Password, …).
      </div>
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      {beginLoading ? (
        <p className="text-center text-sm text-gray-500">Preparing setup…</p>
      ) : (
        <>
          <div className="flex flex-col items-center">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="MFA QR code" className="h-48 w-48" />
            ) : (
              <p className="text-sm text-gray-500">Enter this secret manually in your app:</p>
            )}
            {secret && (
              <p className="mt-2 break-all text-center font-mono text-xs text-gray-600">{secret}</p>
            )}
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label htmlFor="setupCode" className="label">
                Enter the 6-digit code from your app
              </label>
              <input
                id="setupCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                className="input text-center text-2xl tracking-widest"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => {
                  setError('')
                  setCode('')
                  // Offer the passkey choice again when it's available; otherwise
                  // fall back to cancelling out to the sign-in screen.
                  if (passkeyOk) setStep('choose')
                  else onCancel()
                }}
                className="flex-1 btn btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying…' : 'Verify & Continue'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
